import React from "react";
import { Container, Grid, Typography, Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import aboutImage from "../assets/image/landing/heroImage-Lifelog.png";
import colors from "../assets/theme/colors";
import { PRODUCT_NAME_TITLE } from "../constants/config";

export default function About() {
  const navigate = useNavigate();
  const handleGetStarted = () => {
    try {
      const isLoggedIn = Boolean(
        typeof window !== "undefined" &&
          (localStorage.getItem("ce_user") ||
            localStorage.getItem("authUser") ||
            localStorage.getItem("token"))
      );
      navigate(isLoggedIn ? "/dashboard" : "/login", { state: { mode: "signup" } });
    } catch (e) {
      navigate("/login", { state: { mode: "signup" } });
    }
  };

  return (
    <Box
      data-cy="about-page"
      sx={{
        backgroundColor: "background.default",
        pt: { xs: 6, md: 8 },
        pb: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          {/* Left: Text */}
          <Grid item xs={12} sm={7} md={7}>
            <Typography
              component="h1"
              data-cy="about-title"
              sx={{
                fontFamily: '"Dancing Script", cursive',
                color: colors.marketing.about.title,
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "3rem" },
                letterSpacing: "0.5px",
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              About Us
            </Typography>

            <Typography
              component="p"
              sx={{
                color: "text.primary",
                lineHeight: 1.9,
                maxWidth: "62ch",
                fontFamily: '"Poppins", sans-serif',
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: 400,
                mb: 2,
              }}
            >
              At{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {PRODUCT_NAME_TITLE}
              </Box>
              , our mission is simple: make care communication effortless.
              Families and care circles should spend less time chasing updates and
              more time focused on what matters.
            </Typography>

            <Typography
              component="p"
              sx={{
                color: "text.primary",
                lineHeight: 1.9,
                maxWidth: "62ch",
                fontFamily: '"Poppins", sans-serif',
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: 400,
                mb: 2,
              }}
            >
              We started after seeing how easily important details get lost in
              texts, emails, or memory.{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {PRODUCT_NAME_TITLE}
              </Box>{" "}
              helps you quickly capture moments and share them securely so
              everyone stays in sync.
            </Typography>

            <Typography
              component="p"
              sx={{
                color: "text.primary",
                lineHeight: 1.9,
                maxWidth: "62ch",
                fontFamily: '"Poppins", sans-serif',
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: 400,
                mb: 2,
              }}
            >
              While our journey is just beginning, our vision is big. We’re
              building with caregivers, family members, and health professionals
              to ensure the product solves real‑world problems and continues to
              evolve.
            </Typography>

            <Typography
              component="h2"
              sx={{
                mt: 2,
                mb: 1,
                color: "success.main",
                fontWeight: 800,
                fontSize: { xs: "1.3rem", md: "1.4rem" },
                letterSpacing: 0.6,
                textTransform: "uppercase",
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Our Vision
            </Typography>

            <Typography
              component="p"
              sx={{
                color: "text.primary",
                lineHeight: 1.9,
                maxWidth: "62ch",
                fontFamily: '"Poppins", sans-serif',
                fontSize: { xs: "1rem", md: "1.1rem" },
                fontWeight: 400,
                mb: 2,
              }}
            >
              Next, we aim to expand collaborations, add richer media logging,
              and bring more insights for care circles — all while keeping{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {PRODUCT_NAME_TITLE}
              </Box>{" "}
              simple and approachable.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGetStarted}
                data-cy="about-get-started-btn"
                sx={{
                  px: 4,
                  py: 1.4,
                  borderRadius: 2,
                  textTransform: "none",
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 500,
                  boxShadow: `0px 4px 10px ${colors.marketing.about.buttonShadow}`,
                  "&:hover": {
                    boxShadow: `0px 6px 16px ${colors.marketing.about.buttonHoverShadow}`,
                  },
                }}
              >
                Get Started
              </Button>
            </Box>
          </Grid>

          {/* Right: Image */}
          <Grid
            item
            xs={12}
            sm={5}
            md={5}
            data-cy="about-image-column"
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Box
              component="img"
              src={aboutImage}
              alt="Lifelog caregiving illustration"
              sx={{
                width: "100%",
                maxWidth: { xs: 440, sm: 500, md: 600 },
                height: "auto",
                mt: 0,
                ml: { xs: 0, md: 4 },
                borderRadius: 3,
                objectFit: "cover",
                boxShadow: `0px 10px 24px ${colors.marketing.about.imageShadow}`,
                alignSelf: "center",
                display: "block",
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
